import React, { useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { FileUp, X, FileText, Image, File } from "lucide-react";
import { cn } from "../../../lib/utils";
import { Button } from "../../ui";
import type { WizardData } from "../hooks/useWizardState";

interface Props {
  data: WizardData;
  updateData: (patch: Partial<WizardData>) => void;
  onNext: () => void;
  onSkip: () => void;
}

const ACCEPTED_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/webp",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

function getFileIcon(type: string) {
  if (type.startsWith("image/")) return Image;
  if (type.includes("pdf")) return FileText;
  return File;
}

function formatFileSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function DocumentUpload({ data, updateData, onNext, onSkip }: Props) {
  const { t } = useTranslation('addAsset');
  const DOC_TYPES = [
    { value: "warranty", label: t('documents.docTypeWarranty') },
    { value: "receipt",  label: t('documents.docTypeReceipt') },
    { value: "manual",   label: t('documents.docTypeManual') },
    { value: "photo",    label: t('documents.docTypePhoto') },
    { value: "other",    label: t('documents.docTypeOther') },
  ];
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState("");

  const addFiles = useCallback(
    (files: FileList | File[]) => {
      const newFiles: File[] = [];
      const fileArr = Array.from(files);

      for (const file of fileArr) {
        if (!ACCEPTED_TYPES.includes(file.type)) {
          setError(t('documents.errorUnsupported', { name: file.name }));
          continue;
        }
        if (file.size > MAX_FILE_SIZE) {
          setError(t('documents.errorTooLarge', { name: file.name }));
          continue;
        }
        // Prevent duplicates
        if (data.documents.some((d) => d.name === file.name && d.size === file.size)) {
          continue;
        }
        newFiles.push(file);
      }

      if (newFiles.length) {
        setError("");
        updateData({
          documents: [...data.documents, ...newFiles],
          documentTypes: [...(data.documentTypes || []), ...newFiles.map(() => "other")],
        });
      }
    },
    [data.documents, data.documentTypes, updateData, t]
  );

  const removeFile = (index: number) => {
    updateData({
      documents: data.documents.filter((_, i) => i !== index),
      documentTypes: (data.documentTypes || []).filter((_, i) => i !== index),
    });
  };

  const updateDocType = (index: number, type: string) => {
    const updated = [...(data.documentTypes || [])];
    updated[index] = type;
    updateData({ documentTypes: updated });
  };

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragActive(false);
      addFiles(e.dataTransfer.files);
    },
    [addFiles]
  );

  return (
    <div className="animate-fade-in-up">
      <div className="px-6 pb-3 pt-4">
        <h2 className="text-2xl font-light tracking-tight">{t('documents.title')}</h2>
        <p className="text-sm text-muted-foreground mt-1">
          {t('documents.subtitle')}
        </p>
      </div>

      <div className="px-6 pb-6 mt-3 space-y-4">
        {/* Drop zone */}
        <div
          onDrop={onDrop}
          onDragOver={(e) => {
            e.preventDefault();
            setDragActive(true);
          }}
          onDragLeave={() => setDragActive(false)}
          onClick={() => fileInputRef.current?.click()}
          className={cn(
            "flex flex-col items-center justify-center gap-3 p-10 rounded-2xl border-2 border-dashed transition-all cursor-pointer",
            dragActive
              ? "border-primary bg-primary/5"
              : "border-border hover:border-primary/40 hover:bg-muted/50"
          )}
        >
            <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center">
              <FileUp className="h-7 w-7 text-muted-foreground" />
          </div>
          <div className="text-center">
              <p className="text-base font-semibold">{t('documents.clickToUpload')}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {t('documents.formats')}
            </p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_TYPES.join(",")}
            multiple
            className="hidden"
            onChange={(e) => {
              if (e.target.files) addFiles(e.target.files);
              e.target.value = ""; // allow re-upload same file
            }}
          />
        </div>

        {error && (
          <p className="text-sm text-destructive">{error}</p>
        )}

        {/* File list */}
        {data.documents.length > 0 && (
          <div className="space-y-2">
            {data.documents.map((file, idx) => {
              const IconComp = getFileIcon(file.type);
              const docType = (data.documentTypes || [])[idx] || "other";
              return (
                <div
                  key={`${file.name}-${idx}`}
                  className="flex items-center gap-3 p-3 rounded-xl border bg-background shadow-sm animate-fade-in-up"
                  style={{ animationDelay: `${idx * 0.05}s` }}
                >
                  <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center shrink-0">
                    <IconComp className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{file.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatFileSize(file.size)}
                    </p>
                  </div>
                  <select
                    value={docType}
                    onChange={(e) => updateDocType(idx, e.target.value)}
                    className="text-xs border rounded-lg px-2 py-1 bg-background focus:outline-none focus:ring-1 focus:ring-ring shrink-0"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {DOC_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFile(idx);
                    }}
                    className="w-7 h-7 rounded-lg hover:bg-destructive/10 flex items-center justify-center transition-colors"
                  >
                    <X className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive" />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Suggestions */}
        <div className="p-4 rounded-2xl bg-muted/50 border">
          <p className="text-sm font-medium mb-2">{t('documents.suggestedTitle')}</p>
          <ul className="space-y-1.5">
            {[
              t('vault.suggestedWarranty'),
              t('vault.suggestedReceipt'),
              t('vault.suggestedManual'),
              t('vault.suggestedService'),
            ].map((item) => (
              <li
                key={item}
                className="text-xs text-muted-foreground flex items-center gap-2"
              >
                <span className="w-1 h-1 rounded-full bg-muted-foreground/50" />
                {item}
              </li>
            ))}
          </ul>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={onSkip}
            className="flex-1 rounded-xl"
          >
            {t('common.skipForNow')}
          </Button>
          <Button onClick={onNext} className="flex-1 rounded-xl">
            {t('common.continue')}
          </Button>
        </div>
      </div>
    </div>
  );
}
